import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import {
  fmtCurrency,
  fmtCurrencyCompact,
  fmtNumber,
  fmtNumberCompact,
  fmtPercent,
} from '../lib/format.ts';
import { KpiSkeleton } from '../components/Skeleton.tsx';
import { Sparkline } from '../components/Sparkline.tsx';
import { Icon } from '../components/Icon.tsx';

type IconName = 'euro' | 'receipt' | 'reports' | 'store' | 'alertTriangle';

interface Tile {
  label: string;
  value: string;
  sub: string;
  icon: IconName;
  /** Tailwind-Klassen für Icon-Hintergrund + Farbe. */
  tone: string;
  trend?: number[];
  trendColor?: string;
  trendFill?: string;
}

export function KpiTiles() {
  const filters = useFilterStore((s) => s.filters);
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['kpis', filters],
    queryFn: () => api.kpis({ filters }),
  });
  // Sparkline-Daten: rolling Trend
  const tsQ = useQuery({
    queryKey: ['kpi-trend', filters],
    queryFn: () =>
      api.timeseries({
        filters,
        granularity: 'day',
        compareVorjahr: false,
      }),
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <KpiSkeleton key={i} />
        ))}
      </div>
    );
  }
  if (!data) return null;

  const trendUmsatz = (tsQ.data ?? []).map((p) => p.umsatz);
  const trendBons = (tsQ.data ?? []).map((p) => p.anz_bons);

  const tiles: Tile[] = [
    {
      label: 'Umsatz',
      value: fmtCurrencyCompact(data.umsatz_gesamt),
      sub: fmtCurrency(data.umsatz_gesamt),
      icon: 'euro',
      tone: 'bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-400',
      trend: trendUmsatz,
      trendColor: '#1f97f5',
      trendFill: 'rgba(31,151,245,0.15)',
    },
    {
      label: 'Bons',
      value: fmtNumberCompact(data.anz_bons),
      sub: fmtNumber(data.anz_bons),
      icon: 'receipt',
      tone: 'bg-sky-50 text-sky-600 dark:bg-sky-950 dark:text-sky-400',
      trend: trendBons,
      trendColor: '#0ea5e9',
      trendFill: 'rgba(14,165,233,0.15)',
    },
    {
      label: 'Ø-Bon',
      value: fmtCurrency(data.avg_bon),
      sub: 'pro Bon (Brutto)',
      icon: 'reports',
      tone: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-950 dark:text-cyan-400',
    },
    {
      label: 'Filialen',
      value: fmtNumber(data.anz_filialen),
      sub: 'aktiv im Zeitraum',
      icon: 'store',
      tone: 'bg-violet-50 text-violet-600 dark:bg-violet-950 dark:text-violet-400',
    },
    {
      label: 'Storno-Quote',
      value: fmtPercent(data.storno_quote),
      sub: `${fmtNumber(data.anz_stornos)} Stornos`,
      icon: 'alertTriangle',
      tone:
        data.storno_quote > 0.01
          ? 'bg-rose-50 text-rose-600 dark:bg-rose-950 dark:text-rose-400'
          : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4 animate-fade-in">
      {tiles.map((t) => (
        <div key={t.label} className="kpi-tile group">
          <div className="flex items-start justify-between gap-2">
            <div className="kpi-label">{t.label}</div>
            <div className="flex items-center gap-1.5">
              {isFetching && !isLoading && (
                <Icon name="refresh" size={11} className="text-brand-500 animate-spin" />
              )}
              <div
                className={`w-7 h-7 rounded-lg flex items-center justify-center ${t.tone}`}
              >
                <Icon name={t.icon} size={14} />
              </div>
            </div>
          </div>
          <div className="kpi-value" title={t.sub.length < 50 ? t.sub : undefined}>
            {t.value}
          </div>
          <div className="kpi-hint tabular-nums">{t.sub}</div>
          {t.trend && t.trend.length > 1 ? (
            <div className="mt-2 -mx-1 opacity-80 group-hover:opacity-100 transition-opacity">
              <Sparkline data={t.trend} color={t.trendColor!} fillColor={t.trendFill} height={24} />
            </div>
          ) : (
            // Platzhalter für gleiche Höhe wie Sparkline-Tiles
            <div className="mt-2 h-6" />
          )}
        </div>
      ))}
    </div>
  );
}
